import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';

// Generic words that must not be used to match a booking rig to a live feed.
const GENERIC_TOKENS = new Set([
  'rig', 'sabelt', 'treq', 'ace', 'child', 'sim', 'the', 'kit', 'pro', 'one', 'adult', 'carbon',
]);

const tokens = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').split(' ').filter(Boolean);
const distinctive = (s) => tokens(s).filter((t) => !GENERIC_TOKENS.has(t) && !/^\d+$/.test(t));

/**
 * Booking rig keys (e.g. 'sabelt_amg') and the physical RigLiveFeed rig_keys
 * (e.g. 'rig_02') don't line up, so we match on distinctive name tokens
 * ('amg', 'porsche'). Only rigs with a matching live feed get a badge.
 *
 * Returns `statusFor(rig)` → the matched RigLiveFeed record, or null.
 */
export function useLiveRigStatuses() {
  const { data: feeds = [] } = useQuery({
    queryKey: ['live-rig-feeds', 'booking'],
    queryFn: () => base44.entities.RigLiveFeed.list('sort_order'),
    refetchInterval: 30000,
    staleTime: 15000,
  });

  return useCallback(
    (rig) => {
      if (!rig) return null;
      const want = distinctive(`${rig.name || ''} ${rig.key || ''}`);
      if (!want.length) return null;
      return feeds.find((f) => {
        const have = distinctive(f.rig_name);
        return have.some((t) => want.includes(t));
      }) || null;
    },
    [feeds]
  );
}